/**
 * Helper functions for seeding initial data during wallet creation.
 *
 * Extracts Evolu seeding logic from useCreateWallet to keep the hook
 * under the 300-line limit. These are effectively pure functions that
 * operate on a provided Evolu instance.
 *
 * SECURITY: seedNostrIdentity handles private key material. The master
 * mnemonic and derived keys must NEVER be logged.
 *
 * Tier: All tiers (core infrastructure).
 *
 * @module wallet-seed-helpers
 */

import type { Evolu } from '@evolu/common';
import { NonEmptyString1000, SqliteBoolean, getOrThrow } from '@evolu/common';
import { useEvolu } from '@evolu/react';
import { deriveNostrKeypair } from '@/lib/crypto';
import { CONFIG } from '@/lib/config';
import type { SatsflowSchema } from '@/db/database';

/**
 * Cast the generic Evolu instance from useEvolu() to the app's typed schema.
 * useEvolu() returns Evolu<EvoluSchema> (generic), but we need Evolu<SatsflowSchema>
 * for typed insert/update calls. This is safe because the Evolu instance
 * was created with our Schema.
 *
 * @param evolu - Generic Evolu instance from useEvolu()
 * @returns Typed Evolu instance for SatsflowSchema
 */
export function typedEvolu(evolu: ReturnType<typeof useEvolu>): Evolu<SatsflowSchema> {
  return evolu as unknown as Evolu<SatsflowSchema>;
}

/** NIP-06 derivation path identifier for Nostr identity. */
const NOSTR_DERIVATION_PATH = "m/44'/1237'/0'/0/0";

/**
 * Default Cashu mint URL for new wallets.
 *
 * PRODUCT DECISION: This is a placeholder default mint. The product owner
 * should evaluate and confirm the default mint URL for production use.
 * Users can always add/change mints later.
 */
const DEFAULT_CASHU_MINT_URL = CONFIG.defaultMintUrl;

/** Default spending categories seeded for new wallets. */
const DEFAULT_CATEGORIES: ReadonlyArray<{ name: string; color: string; sortOrder: number }> = [
  { name: 'General', color: 'hsl(220, 70%, 50%)', sortOrder: 0 },
  { name: 'Food & Drink', color: 'hsl(25, 85%, 55%)', sortOrder: 1 },
  { name: 'Transport', color: 'hsl(170, 65%, 45%)', sortOrder: 2 },
  { name: 'Shopping', color: 'hsl(330, 70%, 55%)', sortOrder: 3 },
  { name: 'Bills', color: 'hsl(0, 65%, 55%)', sortOrder: 4 },
];

/**
 * Seed default categories into the Evolu database.
 *
 * @param evolu - Evolu instance to insert into
 */
export function seedDefaultCategories(evolu: ReturnType<typeof useEvolu>): void {
  const db = typedEvolu(evolu);
  for (const cat of DEFAULT_CATEGORIES) {
    db.insert('category', {
      name: getOrThrow(NonEmptyString1000.from(cat.name)),
      color: getOrThrow(NonEmptyString1000.from(cat.color)),
      icon: null,
      budget: null,
      isDefault:
        cat.sortOrder === 0 ? getOrThrow(SqliteBoolean.from(1)) : getOrThrow(SqliteBoolean.from(0)),
      sortOrder: cat.sortOrder,
    });
  }
}

/**
 * Seed the user's Nostr identity derived from master mnemonic.
 *
 * @param evolu - Evolu instance to insert into
 * @param masterMnemonic - Master BIP-39 mnemonic (NEVER logged)
 */
export function seedNostrIdentity(
  evolu: ReturnType<typeof useEvolu>,
  masterMnemonic: string,
): void {
  const keypair = deriveNostrKeypair(masterMnemonic);
  const privkeyHex = Array.from(keypair.privateKey)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  typedEvolu(evolu).insert('userIdentity', {
    nostrPrivkey: getOrThrow(NonEmptyString1000.from(privkeyHex)),
    nostrPubkey: getOrThrow(NonEmptyString1000.from(keypair.publicKey)),
    derivationPath: getOrThrow(NonEmptyString1000.from(NOSTR_DERIVATION_PATH)),
    displayName: null,
    lightningAddress: null,
    bolt12Offer: null,
    cashuAddress: null,
  });
}

/**
 * Seed default Cashu mint configuration.
 *
 * @param evolu - Evolu instance to insert into
 */
export function seedDefaultMint(evolu: ReturnType<typeof useEvolu>): void {
  typedEvolu(evolu).insert('cashuMint', {
    url: getOrThrow(NonEmptyString1000.from(DEFAULT_CASHU_MINT_URL)),
    label: getOrThrow(NonEmptyString1000.from(CONFIG.defaultMintLabel)),
    keysets: null,
    trustLevel: getOrThrow(NonEmptyString1000.from('default')),
    isActive: getOrThrow(SqliteBoolean.from(1)),
    lastSyncedAt: null,
    info: null,
  });
}

/**
 * Set onboarding_completed setting in the database.
 *
 * @param evolu - Evolu instance to insert into
 */
export function setOnboardingComplete(evolu: ReturnType<typeof useEvolu>): void {
  typedEvolu(evolu).insert('setting', {
    key: getOrThrow(NonEmptyString1000.from('onboarding_completed')),
    value: getOrThrow(NonEmptyString1000.from('true')),
  });
}
